import { ApolloError } from '@apollo/client'
import { DEFAULT_LANGUAGE, GLOBAL_CONTEXT_MARKER } from '@constants'
import {
  ContextType,
  FieldType,
  ResourceType,
  StringItem,
  StringItemStatus,
} from '@generated/graphql'
import {
  UpsertPostStatus,
  UpsertProduct,
  UpsertProvider,
  UpsertSite,
  UpsertTaxonomyItem,
} from '@generated/operations'
import { triggerErrorMessage } from '@modules/messages'
import { Language } from '@store/slices/languages/types'
import { TemplateField } from '@store/slices/templates/types'
import { GraphQLError } from 'graphql'
import { toUnicode } from 'punycode/'
import { Location } from 'react-router-dom'

import {
  ICreateResourceRedirect,
  IGetContextId,
  IGetEditorLinkWithId,
  IGetFieldByKey,
  IGetFieldsOfType,
  IHasDraftStringByContext,
  IValidateURLReturn,
} from './types'

// TODO: make work with new upserts - CMS-2087
export const setPropertiesOnResource = async ({
  client,
  data,
  id, // Id of resource to update
  type, // Type of resource | provider, product etc
}) => {
  // @ts-ignore
  const { currentNetwork, currentVertical, currentSite } = window

  let mutation,
    current = {
      network: currentNetwork?.id,
      vertical: currentVertical?.id,
      site: currentSite?.id, // TODO: check if this param is required
    }

  if (type === 'Site') {
    mutation = UpsertSite
    //@ts-ignore
    current = {
      network: currentNetwork?.id,
      vertical: currentVertical?.id,
    }
  } else if (type === 'Provider') {
    mutation = UpsertProvider
  } else if (type === 'TaxonomyItem') {
    mutation = UpsertTaxonomyItem
  } else if (type === 'Post') {
    mutation = UpsertPostStatus
  } else if (type === 'TemplateField') {
    throw new Error('TemplateField not implemented in setDataOnField')
  } else if (type === 'Product') {
    mutation = UpsertProduct
  }

  await client.mutate({
    mutation: mutation,
    variables: {
      where: {
        id,
      },
      current,
      data: {
        properties: {
          items: Object.keys(data).map(key => {
            return {
              key,
              data: data?.[key],
            }
          }),
        },
      },
    },
  })
}

export const getContextId = ({
  field,
  currentNetwork,
  currentVertical,
  currentSite,
}: IGetContextId): string | null => {
  if (!field) return null
  if (field.context === ContextType.Global) return GLOBAL_CONTEXT_MARKER
  if (field.context === ContextType.Network) return currentNetwork?.id || null
  if (field.context === ContextType.Company)
    return currentNetwork?.companyId || null
  if (field.context === ContextType.Vertical) return currentVertical?.id || null
  if (field.context === ContextType.Site) return currentSite?.id || null
}

export const getFieldByKey = ({
  key,
  fields = [],
}: IGetFieldByKey): TemplateField => {
  return fields.find(field => field.key === key)
}

export const getFieldsOfType = ({
  type,
  fields,
}: IGetFieldsOfType): TemplateField[] => {
  return fields?.filter(field => field.type === type)
}

export const getFieldResourceType = (
  field: TemplateField,
): ResourceType | null | undefined => {
  if (!field) return null
  switch (field.type) {
    case FieldType.Url:
    case FieldType.Slug:
    case FieldType.Number:
    case FieldType.Post:
    case FieldType.Page:
    case FieldType.Taxonomyitem:
    case FieldType.Product:
    case FieldType.Provider:
    case FieldType.Site:
    case FieldType.Vertical:
    case FieldType.Network:
    case FieldType.Level:
    case FieldType.Template:
    case FieldType.Templatefield:
    case FieldType.Property:
    case FieldType.Language:
    case FieldType.Html:
    case FieldType.Boolean:
    case FieldType.Mediaitem:
    case FieldType.String:
    case FieldType.Pillarsection:
      return ResourceType.Property
    case FieldType.Singleline:
    case FieldType.Multiline:
    case FieldType.Markdown:
    case FieldType.PortableText:
    case FieldType.PortableTextSingleline:
    case FieldType.PortableTextMultiline:
    case FieldType.Stringtemplate:
    case FieldType.UrlContent:
      return ResourceType.Content
    default:
      return
  }
}

export function filterOption(inputValue: string, option: any) {
  return (Object.values(option.searchables) as string[]).some(value =>
    value.toLowerCase().includes(inputValue.toLowerCase()),
  )
}

export function getAddLink(location: Location, pathAddition?: string): string {
  return location.pathname.replace(
    /list|view/,
    `add${pathAddition ? '/' + pathAddition : ''}`,
  )
}

export function getEditorLink(location: Location): string {
  return location.pathname.replace(/list|view/, 'edit')
}

export function getEditorLinkWithId({
  location,
  id,
  subtype,
}: IGetEditorLinkWithId): string {
  const basePath = getEditorLink(location)
  const basePathWithId = basePath + `/${id}`
  const _subtype = subtype === null ? 'default' : subtype
  if (
    location.pathname.includes('taxonom') ||
    location.pathname.includes('product')
  ) {
    return !basePath.includes(subtype)
      ? `${basePath}/${_subtype ? `${_subtype}/` : ''}${id}`
      : basePathWithId
  } else {
    return basePathWithId
  }
}

export function createResourceRedirect({
  pathname,
  navigate,
  location,
}: ICreateResourceRedirect): void {
  navigate({
    pathname,
    search: location.search,
  })
}

export function removeProtocol(url: string): string {
  return url?.replace(/^http(?:s)?:\/\//, '') || ''
}

export function formatDisplayUrl(url?: string | null): string {
  const withoutProtocol = removeProtocol(url || '')
  if (!withoutProtocol) {
    return ''
  }

  const [hostPart, ...rest] = withoutProtocol.split(/([/?#])/)
  const [hostname, port] = hostPart.split(':')

  let decodedHostname = hostname
  try {
    decodedHostname = toUnicode(hostname)
  } catch {
    decodedHostname = hostname
  }

  const suffix = rest.join('')
  return port
    ? `${decodedHostname}:${port}${suffix}`
    : `${decodedHostname}${suffix}`
}

export const validateURL = (isJsonFormat = false): IValidateURLReturn => {
  return value => {
    const url = isJsonFormat ? value && JSON.parse(value)?.value : value
    if (url && !/^https:\/\/.+\..+/.test(url)) {
      return 'Please enter a valid URL'
    }
  }
}

export const checkDisabledPropertyField = (
  currentLanguageTag: Language['languageTag'],
  field: TemplateField,
): boolean => {
  return (
    field?.resourceType === ResourceType.Property &&
    currentLanguageTag !== DEFAULT_LANGUAGE.languageTag &&
    field?.type !== FieldType.Offers
  )
}

export function hasDraftString(strings: StringItem[]): boolean {
  return strings?.some(checkDraft)
}

export function hasDraftStringByContext({
  strings,
  contextId,
}: IHasDraftStringByContext): boolean {
  return strings?.some(
    string =>
      checkDraft(string) &&
      (string.contextId === contextId ||
        string.contextId === GLOBAL_CONTEXT_MARKER),
  )
}

export function checkDraft(string: StringItem): boolean {
  return string.status === StringItemStatus.Draft
}

export const safeJsonParse = (data?: any) => {
  try {
    if (typeof data === 'string') {
      return JSON.parse(data)
    } else if (typeof data === 'object') {
      return data
    }
  } catch (error) {
    if (typeof data === 'string') {
      return data
    }

    return undefined
  }
}

export const onError = (error: GraphQLError | ApolloError): void => {
  return triggerErrorMessage({
    displayType: 'message',
    message: error.message.replace('GraphQL error: ', '').trim(),
  })
}

export const ignoreVariables = (str: string) => {
  return str?.replace(/\[%s:(.*?)\]/gm, '')
}

export function addHttps(str: string): string | undefined {
  const _str = str?.trim()
  if (_str && !_str.startsWith('http')) {
    return 'https://' + _str
  }
  return _str
}
